import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { SystemConfig, MFAPolicy, DEFAULT_MFA_POLICY } from '../types/systemConfig';

const SYSTEM_CONFIG_ID = 'global';

/**
 * Globális rendszer konfiguráció lekérése
 */
export async function getSystemConfig(): Promise<SystemConfig | null> {
  try {
    const docRef = doc(db, 'systemConfig', SYSTEM_CONFIG_ID);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        mfaPolicy: data.mfaPolicy || DEFAULT_MFA_POLICY,
        updatedAt: data.updatedAt?.toDate() || new Date(),
        updatedBy: data.updatedBy || ''
      };
    }

    // Ha még nem létezik, létrehozzuk az alapértelmezett konfigurációt
    const defaultConfig: SystemConfig = {
      id: SYSTEM_CONFIG_ID,
      mfaPolicy: DEFAULT_MFA_POLICY,
      updatedAt: new Date(),
      updatedBy: 'system'
    };

    await setDoc(docRef, {
      mfaPolicy: defaultConfig.mfaPolicy,
      updatedAt: defaultConfig.updatedAt,
      updatedBy: defaultConfig.updatedBy
    });

    return defaultConfig;
  } catch (error) {
    console.error('Error getting system config:', error);
    return null;
  }
}

/**
 * MFA Policy frissítése
 */
export async function updateMFAPolicy(
  policy: Partial<MFAPolicy>,
  userId: string
): Promise<void> {
  try {
    const docRef = doc(db, 'systemConfig', SYSTEM_CONFIG_ID);
    const currentConfig = await getSystemConfig();

    if (!currentConfig) {
      throw new Error('System config not found');
    }

    const updatedPolicy: MFAPolicy = {
      ...currentConfig.mfaPolicy,
      ...policy
    };

    // Ha most kapcsoljuk be a kötelező MFA-t, rögzítjük az időpontot
    if (policy.required && !currentConfig.mfaPolicy.required) {
      updatedPolicy.enforcedAt = new Date();
    }

    await updateDoc(docRef, {
      mfaPolicy: updatedPolicy,
      updatedAt: new Date(),
      updatedBy: userId
    });
  } catch (error) {
    console.error('Error updating MFA policy:', error);
    throw error;
  }
}

/**
 * Ellenőrzi, hogy egy felhasználónak kötelező-e az MFA
 */
export function isMFARequiredForUser(
  mfaPolicy: MFAPolicy,
  userRole: string,
  userCreatedAt?: Date
): { required: boolean; gracePeriodEnds?: Date; daysRemaining?: number } {
  // Ha nincs kötelező MFA, nem kell
  if (!mfaPolicy.required) {
    return { required: false };
  }

  // Ha a felhasználó szerepköre kivétel
  if (mfaPolicy.exemptRoles?.includes(userRole)) {
    return { required: false };
  }

  // Ha van türelmi időszak és a felhasználó új
  if (mfaPolicy.enforcedAt && userCreatedAt) {
    const enforcedDate = new Date(mfaPolicy.enforcedAt);
    const userCreated = new Date(userCreatedAt);

    // Ha a felhasználó a kötelezővé tétel után lett létrehozva
    if (userCreated > enforcedDate) {
      const gracePeriodEnd = new Date(userCreated);
      gracePeriodEnd.setDate(gracePeriodEnd.getDate() + mfaPolicy.gracePeriodDays);

      const now = new Date();
      if (now < gracePeriodEnd) {
        const daysRemaining = Math.ceil((gracePeriodEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        return {
          required: true,
          gracePeriodEnds: gracePeriodEnd,
          daysRemaining
        };
      }
    }
  }

  // Kötelező, nincs türelmi időszak
  return { required: true };
}
